/**
 * posthog-loaded — the ONE signal that the browser PostHog client has
 * initialised (FRIDGE-807).
 *
 * Why it exists. posthog-js drops any `capture()` that arrives before
 * `init()` ("You must initialize PostHog before calling posthog.capture"),
 * and React runs a child's mount effects BEFORE its parent's. PHProvider
 * (app/providers.tsx) calls `init` from its own effect, so any client
 * component that captures from a mount effect — the embed view capture
 * (app/embed/_components/embed-view-capture.tsx) was one — fires first and
 * is silently lost. Measured 09/04/2026: `embed_widget_view` had never
 * existed in the project's event taxonomy; the SDK logged the drop on every
 * framed load. The sitewide page-view capture only survives because it sits
 * inside a Suspense boundary and mounts in a later commit.
 *
 * How it works. The provider's `loaded` callback dispatches one window event;
 * `whenPostHogLoaded` runs the callback immediately when the client is
 * already loaded (client-side navigation, a late mount) and otherwise waits
 * for that event — which is also how a consent-gated visitor's Allow reaches
 * a capture that was waiting. Framework-free so it can be unit-tested with a
 * plain EventTarget.
 */

/** Dispatched on `window` once, by the provider, right after `posthog.init` completes. */
export const POSTHOG_LOADED_EVENT = 'fk:posthog-loaded'

/** The one property posthog-js flips when `init` completes. */
export interface PostHogLoadedLike {
  __loaded?: boolean
}

/** Tell every waiting capture that the client is ready. Safe to call more than once. */
export function announcePostHogLoaded(target: EventTarget | undefined = typeof window === 'undefined' ? undefined : window): void {
  if (!target) return
  target.dispatchEvent(new Event(POSTHOG_LOADED_EVENT))
}

/**
 * Run `fn` once the PostHog client is initialised: now if it already is,
 * otherwise on the provider's loaded event. Returns the unsubscribe so a
 * React effect can clean up on unmount; a no-op when `fn` already ran.
 */
export function whenPostHogLoaded(
  client: PostHogLoadedLike | null | undefined,
  fn: () => void,
  target: EventTarget | undefined = typeof window === 'undefined' ? undefined : window,
): () => void {
  if (!client || !target) return () => {}
  if (client.__loaded) {
    fn()
    return () => {}
  }
  const onLoaded = () => {
    target.removeEventListener(POSTHOG_LOADED_EVENT, onLoaded)
    fn()
  }
  target.addEventListener(POSTHOG_LOADED_EVENT, onLoaded)
  return () => target.removeEventListener(POSTHOG_LOADED_EVENT, onLoaded)
}
